import * as amplitude from "@amplitude/analytics-browser";
import { SearchIcon } from "@chakra-ui/icons";
import {
  Checkbox,
  Flex,
  FormLabel,
  Input,
  InputGroup,
  InputLeftElement,
  SimpleGrid,
  Text,
  VStack,
} from "@chakra-ui/react";
import { useEffect, useState } from "react";
import { ArchivedTag } from "src/scenes/orgAdmin/formTemplates/ContentTab/V2/components/QuestionForm/QuestionExtraInfo/ArchivedTag";
import { Card } from "../Layout/Card";
import { HighlightedText } from "../Text/HighlightedText";
import { TextWithOverflowTooltip } from "../TextWithOverflowTooltip";

export type CheckboxGroupOption = {
  key: string;
  label: string;
  disabled?: boolean;
  tooltipContent?: string;
  isArchived?: boolean;
};

type Props = {
  groupName: string;
  options: CheckboxGroupOption[];
  values: Record<string, boolean>;
  onChange: (key: string, selected: boolean) => void;
  onBatchChange?: (updates: Record<string, boolean>) => void;
  columnWidthMin?: number;
  searchable?: boolean;
  searchPlaceholder?: string;
  searchLabel?: string;
};

export const CheckboxGroup = ({
  groupName,
  options,
  values,
  onChange,
  onBatchChange,
  columnWidthMin = 300,
  searchable = false,
  searchPlaceholder = "Type an option",
  searchLabel = "Search for an option",
}: Props) => {
  const [searchValue, setSearchValue] = useState("");
  const [filteredOptions, setFilteredOptions] = useState(options);
  const searchInputId = `${groupName
    .toLowerCase()
    .replace(/\s+/g, "-")}-search`;

  useEffect(() => {
    if (!searchable || searchValue === "") {
      setFilteredOptions(options);
    } else {
      const filtered = options.filter((option) =>
        option.label.toLowerCase().includes(searchValue.toLowerCase())
      );
      setFilteredOptions(filtered);
    }
  }, [searchValue, options, searchable]);

  const selectableKeys = options
    .filter((option) => !option.disabled)
    .map((option) => option.key);
  const allSelected = options.every((option) => values[option.key]);
  const noneSelected = options.every((option) => !values[option.key]);

  const handleToggleAll = (checked: boolean) => {
    if (onBatchChange) {
      onBatchChange(
        selectableKeys.reduce((acc, key) => ({ ...acc, [key]: checked }), {})
      );
    } else {
      selectableKeys.forEach((key) => onChange(key, checked));
    }
  };

  return (
    <Card showBorder>
      <VStack align="stretch" spacing={searchable ? 2 : 6}>
        <Checkbox
          isChecked={allSelected}
          isIndeterminate={!allSelected && !noneSelected}
          onChange={(e) => {
            amplitude.track("Changed column in view", {
              section_name: groupName,
              view_column_name: `${groupName} - All`,
              active_column: e.target.checked ? "is_active" : "is_inactive",
              checkbox_location: "custom_columns",
            });
            handleToggleAll(e.target.checked);
          }}
        >
          <Text fontWeight="bold">{groupName}</Text>
        </Checkbox>
        {searchable && (
          <VStack align="stretch" mb={2}>
            <FormLabel m={0} fontSize="sm" htmlFor={searchInputId}>
              {searchLabel}
            </FormLabel>
            <InputGroup>
              <InputLeftElement pointerEvents="none">
                <SearchIcon color="gray.400" />
              </InputLeftElement>
              <Input
                id={searchInputId}
                placeholder={searchPlaceholder}
                value={searchValue}
                onChange={(e) => setSearchValue(e.target.value)}
              />
            </InputGroup>
          </VStack>
        )}
        {filteredOptions.length > 0 ? (
          <SimpleGrid minChildWidth={columnWidthMin} spacing={4} columnGap={4}>
            {filteredOptions.map((option) => {
              return (
                <Checkbox
                  key={option.key}
                  isChecked={values[option.key]}
                  isDisabled={option.disabled}
                  onChange={(e) => onChange(option.key, e.target.checked)}
                  sx={{
                    ".chakra-checkbox__label": {
                      overflow: "hidden",
                    },
                  }}
                >
                  <Flex direction="row" gap={2} alignItems="center">
                    {option.isArchived && <ArchivedTag />}
                    <TextWithOverflowTooltip
                      content={option.label}
                      hasArrow
                      defaultTooltipContent={option.tooltipContent}
                      tooltipContentCentered={true}
                    >
                      {searchValue ? (
                        <HighlightedText
                          text={option.label}
                          keyword={searchValue}
                          invert
                        />
                      ) : (
                        option.label
                      )}
                    </TextWithOverflowTooltip>
                  </Flex>
                </Checkbox>
              );
            })}
          </SimpleGrid>
        ) : (
          <Text fontStyle="italic">No options found</Text>
        )}
      </VStack>
    </Card>
  );
};
